import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import GSTModal from './GSTModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface GST {
  gst_id: number;
  gst_name: string;
  gst_cgst: number;
  gst_sgst: number;
  gst_igst: number;
  gst_status: number;
}

const GST: React.FC = () => {
  const [gsts, setGSTs] = useState<GST[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<GST | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchGST = async () => {
    try {
      const data = await apiRequest<GST[]>({
        method: 'get',
        url: 'gsts',
      });
      setGSTs(data);
    } catch (error) {
      showError('Failed to fetch GSTs');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchGST();
  }, []);

  const handleEditClick = (record: GST) => {
    setIsEditMode(true);
    setInitialValues({
      gst_id: record.gst_id,
      gst_name: record.gst_name,
      gst_cgst: record.gst_cgst,
      gst_sgst: record.gst_sgst,
      gst_igst: record.gst_igst,
      gst_status: record.gst_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchGST();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (gst_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-gst/${gst_id}`,
      });
      showSuccess('GST deleted');
      fetchGST();
    } catch {
      showError('Failed to delete GST');
    }
  };

  const handleStatusToggle = async (gstId: number) => {
    setGSTs((prev) =>
      prev.map((item) =>
        item.gst_id === gstId
          ? { ...item, gst_status: item.gst_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-gst/${gstId}`,
      });
      showSuccess('GST status updated');
    } catch (error) {
      showError('Failed to update status');
      setGSTs((prev) =>
        prev.map((item) =>
          item.gst_id === gstId
            ? { ...item, gst_status: item.gst_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<GST>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<GST> = [
    {
      title: 'GST Percentage',
      dataIndex: 'gst_name',
      key: 'gst_name',
      filters: Array.from(new Set(gsts.map((s) => s.gst_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.gst_name || null,
      onFilter: (value, record) => record.gst_name.includes(value as string),
      sorter: (a, b) => a.gst_name.localeCompare(b.gst_name),
      sortOrder: sortedInfo.columnKey === 'gst_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.gst_name}</div>,
    },
    {
      title: 'CGST ',
      dataIndex: 'gst_cgst',
      key: 'gst_cgst',
      filteredValue: filteredInfo.gst_cgst || null,

      // Enable filtering (numeric comparison)
      onFilter: (value, record) => Number(record.gst_cgst) === Number(value),

      // Enable numeric sorting
      sorter: (a, b) => Number(a.gst_cgst) - Number(b.gst_cgst),
      sortOrder: sortedInfo.columnKey === 'gst_cgst' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,

      // Unique CGST filter options
      filters: Array.from(new Set(gsts.map(s => Number(s.gst_cgst)))).map(cgst => ({
        text: `${cgst.toFixed(4)}%`,
        value: cgst,
      })),

      // Proper rendering of CGST values
      render: (text, record) => {
        const cgst = Number(record.gst_cgst);
        return <span>{!isNaN(cgst) ? `${cgst.toFixed(4)}` : '—'}</span>;
      }
    },
    {
      title: 'SGST',
      dataIndex: 'gst_sgst',
      key: 'gst_sgst',
      filteredValue: filteredInfo.gst_sgst || null,

      // Numeric filter
      onFilter: (value, record) => Number(record.gst_sgst) === Number(value),

      // Numeric sort
      sorter: (a, b) => Number(a.gst_sgst) - Number(b.gst_sgst),
      sortOrder: sortedInfo.columnKey === 'gst_sgst' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,

      // Unique SGST filter values
      filters: Array.from(new Set(gsts.map(s => Number(s.gst_sgst)))).map(sgst => ({
        text: `${sgst.toFixed(4)}`,
        value: sgst,
      })),

      // Render SGST to two decimal places
      render: (text, record) => {
        const sgst = Number(record.gst_sgst);
        return <span>{!isNaN(sgst) ? `${sgst.toFixed(4)}` : '—'}</span>;
      }
    },
    {
      title: 'IGST',
      dataIndex: 'gst_igst',
      key: 'gst_igst',
      filteredValue: filteredInfo.gst_igst || null,

      // Numeric filtering (safe conversion)
      onFilter: (value, record) => Number(record.gst_igst) === Number(value),

      // Numeric sorting
      sorter: (a, b) => Number(a.gst_igst || 0) - Number(b.gst_igst || 0),
      sortOrder: sortedInfo.columnKey === 'gst_igst' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,

      // Unique IGST filter values (skip nulls)
      filters: Array.from(new Set(
        gsts.map(s => s.gst_igst).filter(v => v !== null)
      )).map(igst => ({
        text: `${Number(igst).toFixed(4)}`,
        value: Number(igst),
      })),

      // Render IGST value with fallback
      render: (text, record) => {
        const igst = Number(record.gst_igst);
        return <span>{!isNaN(igst) ? `${igst.toFixed(4)}` : '—'}</span>;
      }
    }
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: GST) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.gst_status}
                onChange={() => handleStatusToggle(record.gst_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this GST?"
              onConfirm={() => handleDelete(record.gst_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View GST </Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add GST
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<GST>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="gst_id"
          dataSource={gsts.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No GST found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, gsts.length)} of{' '}
                {gsts.length}
              </span>
              <Pagination
                {...pagination}
                total={gsts.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <GSTModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default GST;